import { yup } from '@framework/validation/initYup.ts';
import { type Feature } from 'ol';
import { type MutableRefObject, useCallback } from 'react';
import { type Subject } from 'rxjs';
import { ExportedMetadataRootKey } from '../../../directives/metadata/IMetadataDirective.ts';
import { MapGeometryItemSchema } from '../createMapSchema.ts';
import { type MapSubjectEvent } from '../MapWidgetComponent.tsx';

interface IUseMapRestoreDefaultHandlerOptions {
  /** Subject per pilotare il ridisegno delle geometrie sulla mappa. */
  mapSubject: MutableRefObject<Subject<MapSubjectEvent>>;
  /** Ricalcolo client-side di intersezioni e competenze dalle geometrie. */
  triggerIntersections: (feature?: Feature) => void;
}

/**
 * Espone `handleRestoreDefault`: ripristina il widget mappa al valore originale
 * FO leggendo SOLO le geometrie dal tag (`root`). Aree, intersezioni e
 * competenze non vengono copiate dal tag ma ricalcolate client-side dalle
 * geometrie via `triggerIntersections` (il BE non ricalcola nulla), così da non
 * trascinare valori potenzialmente disallineati.
 */
export function useMapRestoreDefaultHandler({
  mapSubject,
  triggerIntersections,
}: IUseMapRestoreDefaultHandlerOptions) {
  return useCallback(
    (value: Record<string, unknown>) => {
      // Il valore proviene dal tag (dato esterno): validiamo/coerciamo con lo
      // schema Yup prima di iniettarlo nel form, invece di un cast `as` cieco.
      const geometries =
        yup
          .array(MapGeometryItemSchema)
          .cast(value[ExportedMetadataRootKey] ?? [], { assert: false }) ?? [];

      // Ricarica le geometrie (sincrono: popola `featuresSource`), poi ricalcola
      // intersezioni/competenze dalle nuove geometrie.
      mapSubject.current.next({ type: 'restore', geometries });
      triggerIntersections();
    },
    [mapSubject, triggerIntersections],
  );
}
